import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { initiales } from "@/lib/format";
import { MesRetours } from "@/components/tableaux/Retours";
import { Compteur, ProchaineFiche, MesFichiers, duree } from "./CartesAgent";
import { AgendaJour, Challenge, Activite, MonPipeline, ATraiter } from "./BlocsAgent";

const cumul = (heures, cle) => heures.reduce((acc, h) => [...acc, (acc.at(-1) || 0) + h[cle]], []);
const ecart = (v, ref, unite = "") => { const d = Math.round((v - ref) * 10) / 10; if (!d) return "Dans la moyenne de l'équipe"; return `${d >= 0 ? "▲" : "▼"} ${Math.abs(d)}${unite} ${d >= 0 ? "de plus" : "de moins"} que la moyenne de l'équipe`; };

const PHOTO = "url(https://images.unsplash.com/photo-1776860150305-108ed577d7d4?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70)";
const Pastille = ({ children, testId }) => <span className="at-pill px-3 py-1 text-xs font-semibold" style={{ background: "color-mix(in srgb, var(--sur-primaire, var(--accent-ink)) 16%, transparent)" }} data-testid={testId}>{children}</span>;
const TAILLE_AVATAR = "calc(var(--avatar, 46px) * 1.4)";

export const EnteteAccueil = ({ user, sousTitre, children }) => (
  <section className="at-hero p-5 md:p-7 lg:col-span-12 at-rise" style={{ "--hero-photo": PHOTO }} data-testid="agent-hero">
    <div className="flex items-start gap-4">
      <span className="at-avatar text-xl" style={{ width: TAILLE_AVATAR, height: TAILLE_AVATAR, background: "var(--surface)", color: "var(--menu-fond, var(--menu, var(--accent)))" }}>{initiales(user)}</span>
      <div className="min-w-0">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="accueil-greeting">Bonjour {user.prenom}&nbsp;👋</h1>
        <p className="text-sm mt-1 opacity-90 first-letter:uppercase" data-testid="agent-hero-summary">{sousTitre}</p>
        {children}
      </div>
    </div>
  </section>
);

const Entete = ({ user, d }) => {
  const date = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const pl = (n) => (n > 1 ? "s" : "");
  return (
    <EnteteAccueil user={user} sousTitre={`${date} · ${d.a_appeler} contacts à appeler · ${d.compteur.appels} appels, ${d.manques.length} appel${pl(d.manques.length)} manqué${pl(d.manques.length)}, ${d.rappels.length} rappel${pl(d.rappels.length)} aujourd'hui`}>
      <div className="flex flex-wrap gap-2 mt-3">
        <Pastille testId="agent-hero-goal">🎯 Objectif du jour : {d.objectifs.objectif_opportunites} opportunités</Pastille>
        <span className="at-pill px-3 py-1 text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: "var(--chip-fond, var(--surface))", color: "var(--ink)" }} data-testid="agent-hero-ringover"><span className="w-2 h-2 at-pill" style={{ background: "var(--badge, var(--success))" }} />Ringover · simulation</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-5">
        <Link to="/appels" className="at-btn" style={{ background: "var(--surface)", color: "var(--ink)" }} data-testid="agent-hero-list"><ClipboardList size={17} />Ma liste</Link>
        <Link to="/focus" className="at-btn" style={{ background: "var(--primaire, var(--micro))", color: "var(--sur-primaire, var(--micro-ink))" }} data-testid="agent-hero-start"><Play size={17} />Démarrer ma session d'appels</Link>
      </div>
    </EnteteAccueil>
  );
};

export default function AccueilAgent() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [processus, setProcessus] = useState([]);
  const charger = useCallback(() => api.get("/poste/tableau").then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e))), []);
  useEffect(() => { charger(); api.get("/accueil").then((r) => setProcessus(r.data.processus)).catch(() => {}); }, [charger]);
  if (!d) return <p className="at-muted" data-testid="poste-loading">Chargement de votre accueil…</p>;
  const { compteur: c, objectifs: o, equipe: eq } = d;
  const taux = c.appels ? Math.round((100 * c.decroches) / c.appels) : 0;
  return (
    <div className="grid gap-4 lg:grid-cols-12 items-start" data-testid="page-accueil">
      <Entete user={user} d={d} />
      <div className="lg:col-span-12 min-w-0 empty:hidden"><MesRetours /></div>
      <div className="lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        <Compteur titre="📞 Appels passés" valeur={c.appels} sur={`/ ${o.objectif_appels}`} pct={(100 * c.appels) / o.objectif_appels} couleur="var(--accent)" serie={cumul(d.activite, "appels")} note={ecart(c.appels, eq.appels_moyen)} testId="counter-calls" delai={0} />
        <Compteur titre="✅ Contacts joints" valeur={c.decroches} sur={`· ${taux} %`} pct={(100 * c.decroches) / o.objectif_decroches} couleur="var(--menu, var(--ink))" serie={cumul(d.activite, "joints")} note={`Équipe ${eq.taux_joint} % de décroché`} testId="counter-answered" delai={60} />
        <Compteur titre="🎯 Opportunités" valeur={c.opportunites} sur={`/ ${o.objectif_opportunites}`} pct={(100 * c.opportunites) / o.objectif_opportunites} couleur="var(--success)" note={c.opportunites >= o.objectif_opportunites ? "Objectif du jour atteint" : `Encore ${o.objectif_opportunites - c.opportunites} pour l'objectif`} testId="counter-opportunities" delai={120} />
        <Compteur titre="⏱️ Temps en ligne" valeur={duree(d.temps_ligne_s)} pct={(100 * d.temps_ligne_s) / Math.max(1, eq.temps_ligne_moyen_s * 1.2)} couleur="var(--temp-3)" note={`Équipe ${duree(eq.temps_ligne_moyen_s)} en moyenne`} testId="counter-online" delai={180} />
      </div>
      <ProchaineFiche prochaine={d.prochaine} suivants={d.suivants} />
      <AgendaJour evenements={d.agenda} />
      <Challenge classement={d.classement} objectif={o.objectif_opportunites} />
      <Activite heures={d.activite} objectif={o.objectif_appels} />
      <MonPipeline processus={processus} />
      <ATraiter d={d} recharger={charger} />
      <MesFichiers fichiers={d.fichiers} statuts={d.statuts} />
    </div>
  );
}
